import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Form } from 'antd';
import { useTranslation } from 'react-i18next';
import { CardTitleIcon } from '../CardTitle/CardTitle';

const StyledNav = styled.nav`
  position: sticky;
  top: 16px;
  flex: 0 0 240px;
  align-self: flex-start;
  padding: 16px;
  background: var(--ds-surface);
  border-radius: var(--ds-radius-xl);
  box-shadow: var(--ds-shadow-card);

  .nav-title {
    margin-bottom: 12px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--dimGray);
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    margin-bottom: 4px;
    padding: 10px 12px;
    border: none;
    border-radius: var(--ds-radius-md);
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background var(--ds-dur-fast) var(--ds-ease);

    &:hover {
      background: var(--ds-row-hover);
    }

    &.active {
      background: var(--ds-pear-tint);
    }
  }

  .nav-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    font-size: 13px;
    font-weight: 600;
    color: var(--textColor);
  }

  .nav-progress {
    font-size: 11px;
    font-weight: 400;
    color: var(--dimGray);
  }

  @media (max-width: 992px) {
    display: none;
  }
`;

const isFilled = (value) =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  !(Array.isArray(value) && value.length === 0);

/**
 * Side menu listing the sections of a form: scrolls to a section on click,
 * highlights the visible one and displays the filling progress of each section.
 * @component
 * @param {Object} props - The component props.
 * @param {Object[]} props.sections - Sections: { key, title, icon, selector, fields }.
 * @returns {JSX.Element} - The FormSectionsNav component.
 */
export const FormSectionsNav = ({ sections }) => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState(sections[0]?.key);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((entry) => entry.isIntersecting);
        if (visible) setActiveKey(visible.target.getAttribute('data-section'));
      },
      { rootMargin: '-10% 0px -70% 0px' }
    );
    sections.forEach(({ key, selector }) => {
      const element = document.querySelector(selector);
      if (element) {
        element.setAttribute('data-section', key);
        observer.observe(element);
      }
    });
    return () => observer.disconnect();
  }, [sections]);

  const scrollTo = (key, selector) => {
    setActiveKey(key);
    document
      .querySelector(selector)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <StyledNav>
      <div className="nav-title">{t('global.sections')}</div>
      {sections.map(({ key, title, icon, selector, fields }) => {
        const countable = (fields || []).filter((field) => field.name);

        return (
          <button
            key={key}
            type="button"
            className={`nav-item${activeKey === key ? ' active' : ''}`}
            onClick={() => scrollTo(key, selector)}
          >
            <CardTitleIcon icon={icon} />
            <span className="nav-text">
              {title}
              {countable.length > 0 && (
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => (
                    <span className="nav-progress">
                      {t('global.filled_count', {
                        filled: countable.filter((field) =>
                          isFilled(getFieldValue(field.name))
                        ).length,
                        total: countable.length
                      })}
                    </span>
                  )}
                </Form.Item>
              )}
            </span>
          </button>
        );
      })}
    </StyledNav>
  );
};

FormSectionsNav.propTypes = {
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      icon: PropTypes.string,
      selector: PropTypes.string.isRequired,
      fields: PropTypes.arrayOf(PropTypes.shape({}))
    })
  ).isRequired
};
